import { forwardRef } from 'react'
import type { ComponentPropsWithoutRef, JSX, ReactNode, RefObject } from 'react'

import clsx from 'clsx'

import {
  AllChannelsIcon,
  UsersIcon,
  Button,
  DropdownMenu,
  ExternalLinkIcon,
  FileTextIcon,
  Flex,
  IconButton,
  LightbulbIcon,
  PlusIcon,
  StartPageIcon,
  StatusDot,
  Text,
  Tooltip,
  VisuallyHidden,
  ZapIcon,
} from '@buffer-mono/popcorn'

import styles from './GlobalButton.module.css'

type GlobalButtonMenuProps = {
  triggerRef?: RefObject<HTMLButtonElement>
  className?: string
  isCompact: boolean
  showCollapsedUI: boolean
  hasRecoverablePost: boolean
  onOpen: () => void
  children: ReactNode
}

export function GlobalButtonMenu({
  triggerRef,
  className,
  isCompact,
  showCollapsedUI,
  hasRecoverablePost,
  onOpen,
  children,
}: GlobalButtonMenuProps): JSX.Element {
  return (
    <DropdownMenu onOpenChange={(open) => open && onOpen()}>
      <DropdownMenu.Trigger ref={triggerRef}>
        <GlobalButtonTrigger
          isCompact={isCompact}
          showCollapsedUI={showCollapsedUI}
          label="New"
          compactLabel={
            hasRecoverablePost
              ? 'Create new. You have an unfinished post to recover.'
              : 'Create new'
          }
          hasRecoverablePost={hasRecoverablePost}
          className={className}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content className={styles.dropdownMenu}>
        {children}
      </DropdownMenu.Content>
    </DropdownMenu>
  )
}

type GlobalButtonTriggerProps = Omit<
  ComponentPropsWithoutRef<'button'>,
  'children'
> & {
  isCompact: boolean
  showCollapsedUI: boolean
  label: string
  compactLabel: string
  hasRecoverablePost?: boolean
}

export const GlobalButtonTrigger = forwardRef<
  HTMLButtonElement,
  GlobalButtonTriggerProps
>(function GlobalButtonTrigger(
  {
    isCompact,
    showCollapsedUI,
    label,
    compactLabel,
    hasRecoverablePost = false,
    className,
    ...props
  },
  ref,
) {
  return isCompact ? (
    <IconButton
      ref={ref}
      size="medium"
      variant="primary"
      label={compactLabel}
      className={clsx(showCollapsedUI && styles.globalButton, className)}
      {...props}
    >
      <>
        <PlusIcon />
        {hasRecoverablePost && (
          <StatusDot aria-hidden="true" className={styles.recoveryDot} />
        )}
      </>
    </IconButton>
  ) : (
    <Button
      ref={ref}
      size="large"
      variant="primary"
      className={clsx(styles.globalButton, className)}
      {...props}
    >
      <PlusIcon />
      {label}
      {hasRecoverablePost && (
        <>
          <StatusDot aria-hidden="true" className={styles.recoveryDot} />
          <VisuallyHidden>unfinished post to recover</VisuallyHidden>
        </>
      )}
    </Button>
  )
})

export function GlobalButtonCreatePostItem({
  onCreateNewPost,
  hasRecoverablePost,
}: {
  onCreateNewPost: () => void
  hasRecoverablePost: boolean
}): JSX.Element {
  return (
    <DropdownMenu.Item onClick={onCreateNewPost}>
      <FileTextIcon size="medium" className={styles.postIcon} />
      <Flex direction="column" className={styles.dropdownItemText}>
        <Text as="label" weight="medium">
          Post
        </Text>
        <Text as="small" color="subtle" size="sm" lineHeight="tight">
          Publish content to a channel
        </Text>
      </Flex>
      {hasRecoverablePost && (
        <>
          <StatusDot aria-hidden="true" className={styles.dropdownItemEnd} />
          <VisuallyHidden>unfinished post to recover</VisuallyHidden>
        </>
      )}
    </DropdownMenu.Item>
  )
}

export function GlobalButtonCreateIdeaItem({
  onCreateNewIdea,
}: {
  onCreateNewIdea: () => void
}): JSX.Element {
  return (
    <DropdownMenu.Item onClick={onCreateNewIdea}>
      <LightbulbIcon size="medium" className={styles.ideaIcon} />
      <Flex direction="column" className={styles.dropdownItemText}>
        <Text as="label" weight="medium">
          Idea
        </Text>
        <Text as="small" color="subtle" size="sm" lineHeight="tight">
          Capture a content idea
        </Text>
      </Flex>
    </DropdownMenu.Item>
  )
}

export function GlobalButtonMenuSeparator(): JSX.Element {
  return <DropdownMenu.Separator />
}

export function GlobalButtonConnectChannelItem({
  canConnectChannel,
  onConnectChannel,
}: {
  canConnectChannel: boolean
  onConnectChannel: () => void
}): JSX.Element {
  return canConnectChannel ? (
    <DropdownMenu.Item onClick={onConnectChannel}>
      <AllChannelsIcon size="small" className={styles.menuItemIcon} />
      Connect a New Channel
    </DropdownMenu.Item>
  ) : (
    <Tooltip content="Contact your admin to connect channels" side="right">
      <DropdownMenu.Item disabled>
        <AllChannelsIcon size="small" className={styles.menuItemIcon} />
        Connect a New Channel
      </DropdownMenu.Item>
    </Tooltip>
  )
}

export function GlobalButtonCreateStartPageItem({
  createStartPageHref,
  onCreateStartPage,
}: {
  createStartPageHref: string
  onCreateStartPage: () => void
}): JSX.Element {
  return (
    <DropdownMenu.Item asChild>
      <a
        href={createStartPageHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onCreateStartPage}
      >
        <StartPageIcon
          className={clsx(styles.dropdownItemIcon, styles.menuItemIcon)}
          size="small"
        />
        Create a Start Page
        <ExternalLinkIcon
          className={styles.dropdownItemEnd}
          size="small"
          color="subtle"
        />
      </a>
    </DropdownMenu.Item>
  )
}

export function GlobalButtonInviteTeamMemberItem({
  canManageTeam,
  showInviteTeamUpgradePath,
  onInviteTeamMember,
}: {
  canManageTeam: boolean
  showInviteTeamUpgradePath: boolean
  onInviteTeamMember: () => void
}): JSX.Element {
  return canManageTeam || showInviteTeamUpgradePath ? (
    <DropdownMenu.Item onClick={onInviteTeamMember}>
      <UsersIcon size="small" className={styles.menuItemIcon} />
      Invite a Team Member
      {showInviteTeamUpgradePath && (
        <ZapIcon size="small" className={styles.dropdownItemEnd} />
      )}
    </DropdownMenu.Item>
  ) : (
    <Tooltip content="Contact your admin to invite team members" side="right">
      <DropdownMenu.Item disabled>
        <UsersIcon size="small" className={styles.menuItemIcon} />
        Invite a Team Member
      </DropdownMenu.Item>
    </Tooltip>
  )
}
